import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useAPI from './useAPI';
import env from '../utils/env';

interface UserProfile {
    id: number;
    name: string;
    roles?: string[];
    role?: string[];
}

export default function useUserRole() {
    const auth = useAuth();
    const api = useAPI();
    const [roles, setRoles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
    const cacheRef = useRef<string[] | null>(null);

    const fetchRoles = useCallback(async () => {
        // Return cached roles if available
        if (cacheRef.current) {
            setRoles(cacheRef.current);
            setIsLoading(false);
            return cacheRef.current;
        }

        try {
            setIsLoading(true);
            setError(null);
            const userProfile = await api.Get<UserProfile>('/users/me');

            const fetchedRoles = userProfile?.roles || userProfile?.role || [];
            cacheRef.current = fetchedRoles;
            setCurrentUser(userProfile);
            setRoles(fetchedRoles);
            return fetchedRoles;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user roles';
            setError(errorMessage);
            setRoles([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user) {
            fetchRoles();
        }
    }, [auth.isAuthenticated, auth.user, fetchRoles]);

    const hasRole = useCallback((role: string): boolean => roles.includes(role), [roles]);

    const isAdmin = useCallback((): boolean => {
        if (env.disableAuth) {
            return true;
        }
        return hasRole('ROLE_ADMIN');
    }, [hasRole]);

    return {
        roles,
        isLoading,
        error,
        hasRole,
        isAdmin,
        refetchRoles: fetchRoles,
        currentUser,
    };
}
